import React, { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { Skeleton } from '@ui/components/ui/skeleton';

import { cardClass } from '@app/constants/styles';
import { Language } from '@app/constants/enums';
import { formatDateString, toFixedHours } from '@app/utils';
import { useUserReportsInRange } from '@data/hooks/reports';
import { useListSprints } from '@data/hooks/sprints';

// Insights' own helper: a sprint's window ends the day before the next one starts, not on its `endDate`.
import { SprintWindow, yearSprintWindows } from '../../insights/productTime';
import { productHours, totalMinutes } from './productHours';

interface MemberProductHoursProps {
  organizationId: string;
  userId: string | undefined;
}

const hours = (minutes: number): number => toFixedHours(minutes / 60);

const MemberProductHours = ({ organizationId, userId }: MemberProductHoursProps): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // One fixed "now": a fresh `new Date()` per render lands in the query key, and no request ever settles.
  const today = useMemo(() => new Date(), []);
  const { data: sprintsData, isLoading: sprintsAreLoading, isError: sprintsFailed } = useListSprints({ limit: 200 });

  // Newest first, so the sprint running now is both the default and the top of the list.
  const sprints = useMemo(() => yearSprintWindows(sprintsData?.items ?? [], today).reverse(), [sprintsData, today]);

  const [chosenSprintId, setChosenSprintId] = useState<string>();
  const sprint = sprints.find(candidate => candidate.id === chosenSprintId) ?? sprints[0];

  const range = sprint ? { from: sprint.from, to: sprint.to } : undefined;
  const {
    data: reports,
    isLoading: reportsAreLoading,
    isError: reportsFailed,
  } = useUserReportsInRange(organizationId, userId, range);

  const rows = useMemo(() => productHours(reports), [reports]);
  const total = totalMinutes(rows);
  // Until the sprints land there is no window to ask for, so an empty state here would be a lie.
  const isLoading = sprintsAreLoading || reportsAreLoading;

  const sprintLabel = (candidate: SprintWindow): string =>
    `${t('sprint')} ${candidate.number} (${formatDateString(candidate.from, language as Language, 'dd MMM')} – ${formatDateString(candidate.to, language as Language, 'dd MMM')})`;

  const sprintItems = selectItemsByValue(
    sprints.map(candidate => ({ value: candidate.id, label: sprintLabel(candidate) })),
  );

  return (
    <div className={cardClass}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg font-bold text-foreground">{t('hoursPerProduct')}</h2>
        {sprint ? (
          <Select items={sprintItems} value={sprint.id} onValueChange={value => setChosenSprintId(value ?? undefined)}>
            <SelectTrigger aria-label={t('sprint')} className="w-64 max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sprints.map(candidate => (
                <SelectItem key={candidate.id} value={candidate.id}>
                  {sprintLabel(candidate)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>

      {(() => {
        if (isLoading) return <Skeleton className="h-24 w-full rounded-lg" />;
        // A read that failed is not an answer: no rows would otherwise read as no time filed.
        if (sprintsFailed || reportsFailed)
          return <p className="text-sm text-muted-foreground">{t('cardReadFailed')}</p>;
        // No sprints at all is a different thing from a sprint nobody filed against.
        if (!sprint) return <p className="text-sm text-muted-foreground">{t('memberHours.noSprints')}</p>;
        if (!rows.length) return <p className="text-sm text-muted-foreground">{t('memberHours.noTime')}</p>;

        return (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="pb-2 text-left">{t('titles.product')}</th>
                <th className="pb-2 text-right">{t('memberHours.hours')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.productId} className="border-b border-border">
                  <td className="py-2.5 text-foreground">{row.productName}</td>
                  <td className="py-2.5 text-right tabular-nums text-foreground">{hours(row.minutes)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold">
                <td className="pt-2.5 text-foreground">{t('memberHours.total')}</td>
                <td className="pt-2.5 text-right tabular-nums text-foreground">{hours(total)}</td>
              </tr>
            </tfoot>
          </table>
        );
      })()}
    </div>
  );
};

export default MemberProductHours;
